(function () {
  'use strict';

  /* ============================================================
     HELPERS
     ============================================================ */

  /**
   * Transform Shopify URL to size variant
   * @param {string} src - URL
   * @param {string} size - Size string
   * @returns {string} Resized URL
   */
  function shopifyImageSrc(src, size) {
    /**
     * @typedef {Object} ProductVariant
     * @property {string|number} id - Variant ID
     * @property {string|number} price - Price in cents
     * @property {string|number} [compare_at_price] - Compare at price
     */

    /**
     * @typedef {Object} ProductImage
     * @property {string} src - Image URL
     */

    /**
     * @typedef {Object} ShopifyProduct
     * @property {string|number} id - Product ID
     * @property {string} handle - Slug
     * @property {string} title - Title
     * @property {string} [product_type] - Type
     * @property {boolean} [available] - Available
     * @property {ProductVariant[]} [variants] - Variants
     * @property {ProductImage[]} [images] - Images
     */

    if (!src) return '';
    const queryIdx = src.indexOf('?');
    const path = queryIdx === -1 ? src : src.substring(0, queryIdx);
    const query = queryIdx === -1 ? '' : src.substring(queryIdx);
    const dotIdx = path.lastIndexOf('.');
    // The dot must belong to the filename. Searching the whole URL finds the dot
    // in the hostname when the path has no extension, and splices the size into
    // the host: https://example.com/image -> https://example_300x.com/image.
    if (dotIdx === -1 || dotIdx < path.lastIndexOf('/')) return src;
    return path.substring(0, dotIdx) + '_' + size + path.substring(dotIdx) + query;
  }

  /**
   * Format price in cents to currency string
   * @param {string|number} cents - Cents
   * @param {string} [currency] - Currency code
   * @returns {string} Formatted price
   */
  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
      }).format(parseInt(cents, 10) / 100);
    } catch (_) {
      return '$' + (parseInt(cents, 10) / 100).toFixed(2);
    }
  }

  /**
   * Sanitize product handle to prevent URL injection
   * @param {string} handle - Product handle
   * @returns {string} Sanitized handle
   */
  function sanitizeHandle(handle) {
    if (typeof handle !== 'string') return '';
    // Shopify handles are lowercase alphanumeric + hyphens only
    // Reject any input containing non-alphanumeric, non-hyphen characters
    return /^[a-z0-9-]+$/.test(handle) ? handle : '';
  }

  /**
   * Validate and sanitize API response structure
   * @param {*} data - API response data
   * @returns {boolean} Is valid
   */
  function isValidProductsJsonResponse(data) {
    if (!data || typeof data !== 'object') return false;
    if (!Array.isArray(data.products)) return false;
    return data.products.every(function (product) {
      return product && typeof product === 'object' && typeof product.id !== 'undefined';
    });
  }

  /**
   * Build gallery card DOM element
   * @param {ShopifyProduct} product - Product
   * @param {number} index - Position
   * @param {string} currency - Currency
   * @returns {HTMLArticleElement} Card
   */
  function buildCard(product, index, currency) {
    try {
      if (!product || typeof product !== 'object') {
        console.error('[collection-museum-gallery] Invalid product object:', product);
        return null;
      }
      // Validate required fields
      if (typeof product.id === 'undefined' || product.id === null) {
        console.error('[collection-museum-gallery] Product missing id:', product);
        return null;
      }
      const isAvailable = product.available !== false;
      const isMulti = product.variants && product.variants.length > 1;
      const variant = (product.variants && product.variants[0]) || {};
      const price = variant.price || '0';
      const comparePrice = variant.compare_at_price;
      const onSale = comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10);
      const image = product.images && product.images[0];
      const numStr = index < 10 ? '0' + index : '' + index;

      // Sanitize product handle to prevent URL injection attacks
      const sanitizedHandle = sanitizeHandle(product.handle);
      const productUrl = sanitizedHandle ? '/products/' + sanitizedHandle : '#invalid';
      if (!sanitizedHandle && product.handle) {
        console.warn(
          '[collection-museum-gallery] Product handle invalid (potential injection):',
          product.id,
          product.handle
        );
      }

      const article = document.createElement('article');
      article.className = 'mg-card adc-card';
      article.setAttribute('role', 'listitem');
      article.setAttribute('data-product-id', product.id);
      article.setAttribute('data-product-url', productUrl);

      const media = document.createElement('div');
      media.className = 'mg-card__media';
      if (image) {
        const img = document.createElement('img');
        img.className = 'mg-card__img';
        img.src = shopifyImageSrc(image.src, '800x');
        img.alt = product.title || '';
        img.width = 800;
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'mg-card__media--placeholder';
        media.appendChild(placeholder);
      }
      article.appendChild(media);

      if (!isAvailable) {
        const badge = document.createElement('span');
        badge.className = 'mg-card__soldout';
        badge.textContent = 'Sold Out';
        article.appendChild(badge);
      }

      const num = document.createElement('span');
      num.className = 'mg-card__num';
      num.setAttribute('aria-hidden', 'true');
      num.textContent = numStr;
      article.appendChild(num);

      const overlay = document.createElement('div');
      overlay.className = 'mg-card__overlay';

      const info = document.createElement('div');
      info.className = 'mg-card__info';

      // Validate and sanitize product_type (can contain user input via product editing)
      if (product.product_type) {
        const productType = String(product.product_type).trim();
        // Reject suspicious patterns (HTML, script tags, excessively long strings)
        if (productType.length > 0 && productType.length <= 255 && !/[<>"'`]/.test(productType)) {
          const cat = document.createElement('span');
          cat.className = 'mg-card__category';
          cat.textContent = productType; // textContent prevents XSS
          info.appendChild(cat);
        }
      }

      const titleEl = document.createElement('h2');
      titleEl.className = 'mg-card__title';
      const titleLink = document.createElement('a');
      titleLink.href = productUrl;
      titleLink.textContent = product.title || '';
      titleEl.appendChild(titleLink);
      info.appendChild(titleEl);

      const priceEl = document.createElement('p');
      priceEl.className = 'mg-card__price';
      if (onSale) {
        const compare = document.createElement('span');
        compare.className = 'mg-card__compare';
        compare.textContent = formatMoney(comparePrice, currency);
        const sale = document.createElement('span');
        sale.className = 'mg-card__sale-price';
        sale.textContent = formatMoney(price, currency);
        priceEl.appendChild(compare);
        priceEl.appendChild(sale);
      } else {
        priceEl.textContent = formatMoney(price, currency);
      }
      info.appendChild(priceEl);

      overlay.appendChild(info);

      // requires_selling_plan is an Admin API-only field and is absent from the
      // storefront products.json response. Fall back to tag detection for AJAX-loaded cards.
      const requiresSellingPlan =
        product.requires_selling_plan === true ||
        (Array.isArray(product.tags) && product.tags.includes('craft_subscription'));
      if (requiresSellingPlan || isMulti) {
        const btn = document.createElement('a');
        btn.className = 'mg-btn js-atc';
        btn.href = productUrl;
        btn.target = '_blank';
        btn.rel = 'noopener noreferrer';
        btn.setAttribute('data-action', 'view');
        btn.textContent = requiresSellingPlan ? 'Subscribe' : 'View Product';
        overlay.appendChild(btn);
      } else if (!isAvailable) {
        const btn = document.createElement('span');
        btn.className = 'mg-btn';
        btn.style.cssText = 'opacity:0.4;pointer-events:none;';
        btn.textContent = 'Sold Out';
        overlay.appendChild(btn);
      } else {
        const btn = document.createElement('button');
        btn.className = 'mg-btn js-atc';
        btn.setAttribute('data-variant-id', variant.id || '');
        btn.type = 'button';
        btn.textContent = 'Add to Collection';
        overlay.appendChild(btn);
      }

      article.appendChild(overlay);
      return article;
    } catch (err) {
      console.error('[collection-museum-gallery] Error building card for product:', product, err);
      return null;
    }
  }

  /* ============================================================
     INIT
     ============================================================ */

  /**
   * Initialize museum gallery with all interactive features
   * Entry point for the museum gallery view (collections/all?view=museum-gallery).
   *
   * Features initialized (in order):
   *   1. Scroll progress bar — throttled RAF updates to .mg-progress width
   *   2. Header hide-on-scroll — smooth translateY(-100%) when scrolling down >80px
   *   3. Custom cursor — desktop-only, desktop only, position tracked via RAF
   *   4. Card reveal animations — IntersectionObserver with 400px bottom margin
   *   5. 3D tilt + gloss — delegated to acrylic-depth-card.js via [data-acrylic-tilt]
   *   6. Overflow loader — infinite scroll via sentinel element + products.json pagination
   *   7. Card click handling — click card (not link/button) → open product in new tab
   *   8. Add to cart — click .js-atc button → POST /cart/add.js → update cart count + publish PubSub
   *   9. Cart count display — cache DOM queries, publish FoxTheme PubSub events on update
   *
   * Scroll container: .mg-wrapper has overflow-x:hidden which makes overflow-y:auto by CSS spec.
   * All scroll listeners attach to wrapper, not window. See CSS comment for details.
   *
   * @returns {void}
   */
  function init() {
    const wrapper = document.getElementById('MuseumGallery');
    if (!wrapper) return;

    const gallery = wrapper.querySelector('.mg-gallery');
    const progress = wrapper.querySelector('.mg-progress');
    const sentinel = wrapper.querySelector('.mg-sentinel');
    const cursor = wrapper.querySelector('.mg-cursor');

    // Collect window/document listener refs so we can remove them when the gallery
    // is no longer on the page (Fox theme uses AJAX navigation — page:loaded fires
    // without re-executing this script, leaving zombie listeners on window/document).
    const globalListeners = [];
    const cursorCircle = cursor ? cursor.querySelector('.mg-cursor__circle') : null;
    const cursorLabel = cursor ? cursor.querySelector('.mg-cursor__label') : null;

    if (!gallery) return;

    /* ============================================================
       SCROLL PROGRESS BAR (throttled via RAF)
       ============================================================ */

    /* .mg-wrapper has min-height:100vh with no fixed height — it grows with content and
       never scrolls. The window is the scroll container. All scroll listeners attach to
       window, not wrapper. progress.style.transform (scaleX) is GPU-composited; width is not. */

    if (progress) {
      /**
       * Read phase: turn the shared scroll metrics into a scale factor.
       * No document geometry is read here beyond what the coordinator already
       * measured once for the whole page.
       *
       * @param {object} m - Shared scroll metrics from window.CoraScroll.
       * @returns {number} The scaleX factor for the progress bar.
       */
      function readMuseumScrollProgress(m) {
        const scrollHeight = m.scrollHeight - m.innerHeight;
        return scrollHeight > 0 ? m.scrollY / scrollHeight : 0;
      }

      /**
       * Write phase: apply the scale factor. transform: scaleX() is
       * compositor-only (no layout), unlike width.
       *
       * @param {number} scale - The scaleX factor computed in the read phase.
       */
      function writeMuseumScrollProgress(scale) {
        progress.style.transform = 'scaleX(' + scale + ')';
      }

      const unsubscribeProgress = window.CoraScroll.subscribe({
        name: 'museum-gallery-scroll-progress',
        read: readMuseumScrollProgress,
        write: writeMuseumScrollProgress,
      });
      globalListeners.push(['coraScroll:progress', unsubscribeProgress, null]);
      if (window.__DEBUG_GALLERY) {
        console.warn('[collection-museum-gallery] Progress bar initialized');
      }
    }

    /* ============================================================
       HEADER HIDE-ON-SCROLL (museum gallery only)
       ============================================================ */

    let headerLastScrollY = 0;
    let isHeaderHidden = false;
    let headerToggleCount = 0;

    /**
     * Animate header visibility based on scroll direction and position
     * Hidden condition: scrolling down AND have scrolled past threshold (80px).
     * Prevents flickering on initial page load by requiring threshold.
     * Smooth 0.3s ease-in/out transition, and sets pointerEvents to 'none' while hidden.
     *
     * @param {boolean} shouldHide - Whether to hide header
     */
    function updateHeaderVisibility(shouldHide) {
      if (shouldHide === isHeaderHidden) return; // Avoid redundant DOM updates
      isHeaderHidden = shouldHide;
      headerToggleCount++;
      const headerElement = document.querySelector('.header-section');
      if (!headerElement) {
        console.warn(
          '[collection-museum-gallery] Header element not found when trying to toggle visibility'
        );
        return;
      }
      headerElement.style.transition = 'transform 0.3s ease';
      headerElement.style.transform = shouldHide ? 'translateY(-100%)' : 'translateY(0)';
      headerElement.style.pointerEvents = shouldHide ? 'none' : '';

      if (window.__DEBUG_GALLERY) {
        console.warn('[collection-museum-gallery] Header visibility toggled', {
          hidden: shouldHide,
          toggleCount: headerToggleCount,
          scrollY: window.scrollY,
        });
      }
    }

    /**
     * Read phase: take scrollY from the shared metrics, no geometry of its own.
     *
     * @param {object} m - Shared scroll metrics from window.CoraScroll.
     * @returns {number} The current scrollY.
     */
    function readMuseumHeaderScroll(m) {
      return m.scrollY;
    }

    /**
     * Write phase: detect scroll direction and update header visibility.
     * Compare current vs last scroll position: if currentY > lastY, user is
     * scrolling down.
     *
     * @param {number} currentY - The scrollY computed in the read phase.
     */
    function writeMuseumHeaderScroll(currentY) {
      updateHeaderVisibility(currentY > headerLastScrollY && currentY > 80);
      headerLastScrollY = currentY;
    }

    const unsubscribeHeader = window.CoraScroll.subscribe({
      name: 'museum-gallery-header-hide',
      read: readMuseumHeaderScroll,
      write: writeMuseumHeaderScroll,
    });
    globalListeners.push(['coraScroll:header', unsubscribeHeader, null]);

    /* ============================================================
       CUSTOM CURSOR (desktop only)
       ============================================================ */

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (isDesktop && cursor && cursorCircle && cursorLabel) {
      let customCursorX = -200;
      let customCursorY = -200;
      let cursorMoveCount = 0;
      let cursorEnterTime = null;

      const updateCustomCursor = window.GalleryUtils.createRafThrottle(function () {
        cursor.style.transform =
          'translate(calc(' + customCursorX + 'px - 50%), calc(' + customCursorY + 'px - 50%))';
        cursorMoveCount++;
      });

      const onMouseMove = function (e) {
        customCursorX = e.clientX;
        customCursorY = e.clientY;
        updateCustomCursor();
      };
      document.addEventListener('mousemove', onMouseMove);
      globalListeners.push(['mousemove', onMouseMove, document]);

      wrapper.addEventListener('mouseenter', function () {
        cursor.style.opacity = '1';
        cursorEnterTime = performance.now();
        if (window.__DEBUG_GALLERY) {
          console.warn('[collection-museum-gallery] Custom cursor entered gallery');
        }
      });

      wrapper.addEventListener('mouseleave', function () {
        cursor.style.opacity = '0';
        if (cursorEnterTime && window.__DEBUG_GALLERY) {
          const durationMs = performance.now() - cursorEnterTime;

          console.warn('[collection-museum-gallery] Custom cursor left gallery', {
            durationMs: durationMs.toFixed(0),
            movesRecorded: cursorMoveCount,
          });
          cursorMoveCount = 0;
        }
      });
    }

    /* ============================================================
       CARD REVEAL ANIMATIONS (IntersectionObserver, 400px lazy load)
       ============================================================ */

    /**
     * Observe cards for entry into viewport and add .is-visible class
     * Uses IntersectionObserver with 400px bottom margin to trigger animation ~400px before card becomes visible.
     * This creates a staggered reveal effect as user scrolls (cards fade in before reaching viewport).
     *
     * Fallback for IE11: if IntersectionObserver not supported, immediately show all cards.
     *
     * Unobserves each card after first intersection to prevent repeated classList operations.
     *
     * @param {HTMLElement[]} cards - Array of card elements to observe
     */
    function observeCards(cards) {
      if (!('IntersectionObserver' in window)) {
        // Fallback for IE11/older browsers: show cards immediately
        if (window.__DEBUG_GALLERY) {
          console.warn(
            '[collection-museum-gallery] IntersectionObserver not supported, showing cards immediately'
          );
        }
        cards.forEach(function (card) {
          card.classList.add('is-visible');
        });
        return;
      }

      let revealedCount = 0;
      const revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealedCount++;

              if (window.__DEBUG_GALLERY) {
                console.warn('[collection-museum-gallery] Card revealed', {
                  cardId: entry.target.dataset.productId || 'unknown',
                  revealedTotal: revealedCount,
                });
              }
              revealObserver.unobserve(entry.target); // Cleanup: no longer need to observe this card
            }
          });
        },
        {
          root: null, // Use viewport as root
          rootMargin: '0px 0px 400px 0px', // Trigger 400px before bottom of viewport
          threshold: 0, // Fire when any part becomes visible
        }
      );

      cards.forEach(function (card) {
        revealObserver.observe(card);
      });
      if (window.__DEBUG_GALLERY) {
        console.warn(
          '[collection-museum-gallery] Observing ' + cards.length + ' cards for reveal animations'
        );
      }
    }

    const initialCards = Array.prototype.slice.call(gallery.querySelectorAll('.mg-card'));
    if (initialCards.length > 0) {
      initialCards[0].classList.add('is-visible');
      observeCards(initialCards.slice(1));
    }

    /* ============================================================
       3D TILT + GLOSS handled by acrylic-depth-card.js via
       [data-acrylic-tilt] on .mg-gallery.
       ============================================================ */

    /* ============================================================
       OVERFLOW LOADER — Infinite scroll via products.json (>250 items)
       ============================================================ */

    /**
     * Infinite scroll pagination for collections exceeding 250 initial items.
     * Shopify renders first 250 products server-side (performance). Remaining products
     * fetched on-demand via /collections/{handle}/products.json?page=2,3,... as user scrolls.
     *
     * Sentinel pattern: IntersectionObserver watches .mg-sentinel element (positioned after cards).
     * When sentinel enters viewport (500px margin), load next page of products.
     * Continues until loadedCount >= totalCount or products.json returns empty page.
     *
     * State: currentPage (starts 2), loadedCount (starts at server render count), isFetching (prevents duplicate requests).
     * On success: append new cards to gallery, observe for reveal animations, increment page.
     * On error: show .is-error state on sentinel, log error, stop loading.
     */

    const collectionHandle = gallery.dataset.collectionHandle || '';
    const totalCount = parseInt(gallery.dataset.totalCount || '0', 10);
    const currency = gallery.dataset.currency || 'USD';
    const serverRenderedCount = initialCards.length;
    const pageSize = parseInt(gallery.dataset.pageSize || '48', 10);
    // minimal/low devices use an explicit "Load More" button; mid/flagship use IntersectionObserver
    const deviceTier = document.documentElement.dataset.deviceTier;
    const usePaginationButton = deviceTier === 'minimal' || deviceTier === 'low';

    if (sentinel && collectionHandle && totalCount > serverRenderedCount) {
      let currentPage = 2;
      let loadedCount = serverRenderedCount;
      let isFetching = false;
      let overflowObserver = null;
      let loadMoreBtn = null;

      function loadNextPage() {
        if (isFetching) return;
        isFetching = true;
        sentinel.classList.add('is-loading');
        const fetchStartTime = performance.now();

        const safeCollectionHandle = sanitizeHandle(collectionHandle);
        if (!safeCollectionHandle) {
          isFetching = false;
          sentinel.classList.remove('is-loading');
          console.error('[collection-museum-gallery] Sanitization failed', {
            originalHandle: collectionHandle,
          });
          return;
        }

        const fetchUrl =
          '/collections/' +
          safeCollectionHandle +
          '/products.json?limit=' +
          pageSize +
          '&page=' +
          currentPage;
        if (window.__DEBUG_GALLERY) {
          console.warn('[collection-museum-gallery] Fetching overflow page', {
            page: currentPage,
            url: fetchUrl,
            loadedSoFar: loadedCount,
            totalExpected: totalCount,
          });
        }

        window.GalleryUtils.fetchJson(fetchUrl)
          .then(function (data) {
            const fetchDurationMs = performance.now() - fetchStartTime;
            if (!isValidProductsJsonResponse(data)) {
              throw new Error(
                '[collection-museum-gallery] Invalid products.json response structure'
              );
            }
            const products = data.products;
            const newCards = [];

            products.forEach(function (product) {
              loadedCount++;
              const card = buildCard(product, loadedCount, currency);
              if (card) {
                gallery.appendChild(card);
                newCards.push(card);
              }
            });

            if (newCards.length > 0) observeCards(newCards);

            currentPage++;
            isFetching = false;
            sentinel.classList.remove('is-loading');

            if (window.__DEBUG_GALLERY) {
              console.warn('[collection-museum-gallery] Overflow page loaded', {
                page: currentPage - 1,
                itemsLoaded: products.length,
                totalLoaded: loadedCount,
                fetchDurationMs: fetchDurationMs.toFixed(0),
              });
            }

            const allLoaded = loadedCount >= totalCount || products.length === 0;
            if (allLoaded) {
              sentinel.classList.add('is-end');
              if (loadMoreBtn) loadMoreBtn.style.display = 'none';
              if (overflowObserver) overflowObserver.unobserve(sentinel);
              if (window.__DEBUG_GALLERY) {
                console.warn('[collection-museum-gallery] Overflow loading complete', {
                  totalLoaded: loadedCount,
                  reason: loadedCount >= totalCount ? 'all items loaded' : 'empty page received',
                });
              }
            } else if (loadMoreBtn) {
              loadMoreBtn.disabled = false;
              loadMoreBtn.textContent = 'Load More';
            }
          })
          .catch(function (err) {
            isFetching = false;
            sentinel.classList.remove('is-loading');
            const fetchDurationMs = performance.now() - fetchStartTime;
            console.error('[collection-museum-gallery] Overflow load error', {
              page: currentPage,
              url: fetchUrl,
              loadedSoFar: loadedCount,
              error: err.message,
              stack: err.stack,
              durationMs: fetchDurationMs.toFixed(0),
            });
            sentinel.classList.add('is-error');
            if (loadMoreBtn) {
              loadMoreBtn.disabled = false;
              loadMoreBtn.textContent = 'Retry';
            }
          });
      }

      if (usePaginationButton) {
        // Low/minimal: explicit button — no IntersectionObserver overhead, user controls load pace
        loadMoreBtn = document.createElement('button');
        loadMoreBtn.className = 'mg-load-more';
        loadMoreBtn.type = 'button';
        loadMoreBtn.textContent = 'Load More';
        sentinel.insertAdjacentElement('afterend', loadMoreBtn);
        loadMoreBtn.addEventListener('click', function () {
          loadMoreBtn.disabled = true;
          loadMoreBtn.textContent = 'Loading…';
          loadNextPage();
        });
      } else {
        // Mid/flagship: automatic infinite scroll
        overflowObserver = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting || isFetching || loadedCount >= totalCount) return;
            loadNextPage();
          },
          { root: null, rootMargin: '0px 0px 500px 0px', threshold: 0 }
        );
        overflowObserver.observe(sentinel);
      }
    } else if (sentinel) {
      sentinel.classList.add('is-end');
    }

    /* ============================================================
       CARD CLICK → OPEN PRODUCT IN NEW TAB
       ============================================================ */

    /**
     * Click handler: Open product when user clicks card (not link/button)
     * If click target is a link or button, let it handle naturally (don't override).
     * Otherwise, treat click as intent to view product → open in new tab with noopener, noreferrer.
     */
    gallery.addEventListener('click', function (e) {
      if (e.target.closest('a, button')) return; // Let links/buttons handle naturally
      const card = e.target.closest('.mg-card');
      if (!card || !card.dataset.productUrl) return;
      window.open(card.dataset.productUrl, '_blank', 'noopener,noreferrer');
    });

    /* ============================================================
       ADD TO CART
       ============================================================ */

    /**
     * Add-to-cart handler: Click .js-atc button → POST to /cart/add.js → update UI
     * Two button types:
     *   1. Single-variant products: button with data-variant-id → add to cart
     *   2. Multi-variant products: link with data-action="view" → redirect to product page
     *   3. Sold-out products: disabled button (opacity: 0.4, pointer-events: none)
     *
     * Flow: Add loading state → validate variant ID → POST /cart/add.js → update cart count → show success state (2s) → restore original state.
     * Error handling: Show "TRY AGAIN" label and log the error; the label persists (no auto-revert) until the customer clicks again.
     */
    let atcAttemptCount = 0;

    gallery.addEventListener('click', function (e) {
      const btn = e.target.closest('.js-atc');
      if (!btn) return;
      if (btn.dataset.action === 'view') return; // Multi-variant redirect, let link handle it
      if (btn.classList.contains('is-loading')) return; // Prevent duplicate submission

      const variantId = btn.dataset.variantId;
      if (!variantId) {
        console.warn('[collection-museum-gallery] Add to cart button missing variantId');
        return;
      }

      btn.classList.add('is-loading');
      btn.textContent = 'ADDING...';
      atcAttemptCount++;

      // Validate variant ID before sending to API (must be positive integer)
      const parsedVariantId = parseInt(variantId, 10);
      if (isNaN(parsedVariantId) || parsedVariantId <= 0) {
        btn.classList.remove('is-loading');
        btn.textContent = 'INVALID VARIANT';
        console.error('[collection-museum-gallery] Invalid variant ID', {
          raw: variantId,
          parsed: parsedVariantId,
          attemptNumber: atcAttemptCount,
        });
        setTimeout(function () {
          btn.textContent = 'Add to Collection';
        }, 2000);
        return;
      }

      // POST variant to Shopify cart endpoint
      const cartAddStartTime = performance.now();
      if (window.__DEBUG_GALLERY) {
        console.warn('[collection-museum-gallery] Add to cart started', {
          variantId: parsedVariantId,
          attemptNumber: atcAttemptCount,
        });
      }

      window.GalleryUtils.fetchJson('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: parsedVariantId, quantity: 1 }),
      })
        .then(function (data) {
          const cartAddDurationMs = performance.now() - cartAddStartTime;
          btn.classList.remove('is-loading');
          // SECURITY: Validate response has expected structure before use
          if (data && typeof data === 'object' && typeof data.id !== 'undefined') {
            // Success: show "ADDED" state for 2s, then restore
            btn.classList.add('is-added');
            btn.textContent = '✓ Added!';
            if (window.__DEBUG_GALLERY) {
              console.warn('[collection-museum-gallery] Add to cart success', {
                variantId: parsedVariantId,
                responseId: data.id,
                durationMs: cartAddDurationMs.toFixed(0),
              });
            }
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', { detail: { product: data } })
            );
            (function openCartAfterAdd() {
              function tryOpenDrawer() {
                const drawer = document.querySelector('cart-drawer');
                if (drawer && typeof drawer.show === 'function') {
                  requestAnimationFrame(function () {
                    requestAnimationFrame(function () {
                      drawer.show(null);
                    });
                  });
                }
              }
              if (window.CoraCartRefresh && typeof window.CoraCartRefresh.refresh === 'function') {
                try {
                  window.CoraCartRefresh.refresh();
                } catch (_e) {
                  // refresh failed; fall through to fetchCartState
                  if (window.CoraCart && typeof window.CoraCart.fetchCartState === 'function') {
                    window.CoraCart.fetchCartState().then(function (cart) {
                      if (cart) window.CoraCart.publishCartUpdate(cart);
                      tryOpenDrawer();
                    });
                    return;
                  }
                }
                tryOpenDrawer();
              } else if (window.CoraCart && typeof window.CoraCart.fetchCartState === 'function') {
                window.CoraCart.fetchCartState().then(function (cart) {
                  if (cart) window.CoraCart.publishCartUpdate(cart);
                  tryOpenDrawer();
                });
              } else {
                const fallbackTimer = setTimeout(tryOpenDrawer, 2000);
                updateCartCount().finally(function () {
                  clearTimeout(fallbackTimer);
                  tryOpenDrawer();
                });
              }
            })();
            setTimeout(function () {
              btn.classList.remove('is-added');
              btn.textContent = 'Add to Collection';
            }, 2000);
          } else {
            // Unexpected response structure: show "TRY AGAIN" and leave it interactive.
            // BUG FIX: previously auto-reverted to "Add to Collection" after 2s via setTimeout,
            // which made a failed add-to-cart indistinguishable from one that never happened if
            // the customer wasn't looking at the exact right moment. The button stays on
            // "TRY AGAIN" (already interactive — is-loading was never set here) until the
            // customer clicks it again, which restarts the normal add-to-cart flow.
            btn.textContent = 'TRY AGAIN';
            console.warn('[collection-museum-gallery] Add to cart unexpected response', {
              variantId: parsedVariantId,
              response: data,
              durationMs: cartAddDurationMs.toFixed(0),
            });
          }
        })
        .catch(function (err) {
          const cartAddDurationMs = performance.now() - cartAddStartTime;
          btn.classList.remove('is-loading');

          // Detect Shopify "requires selling plan" 422 → redirect to product page gracefully
          const errMsg = (err.message || '').toLowerCase();
          const isSPError =
            err.status === 422 ||
            errMsg.includes('selling plan') ||
            errMsg.includes('selling_plan');

          if (isSPError) {
            const card = btn.closest('[data-product-url]');
            const cardUrl = card && card.dataset.productUrl;
            btn.textContent = 'VIEW PRODUCT →';
            console.warn(
              '[collection-museum-gallery] Subscription product detected — redirecting',
              {
                variantId: parsedVariantId,
                productUrl: cardUrl,
              }
            );
            setTimeout(function () {
              if (cardUrl && cardUrl !== '#invalid') {
                window.open(cardUrl, '_blank', 'noopener,noreferrer');
              } else {
                btn.textContent = 'Add to Collection';
              }
            }, 800);
            return;
          }

          // Generic network/server/timeout error: show a retry-able error and stay interactive.
          // BUG FIX: this used to show "ERROR" for exactly 2s then auto-revert to
          // "Add to Collection" via setTimeout. After a 12s timeout wait, a customer who
          // wasn't staring at the button during that 2s window would only ever see the
          // button flicker back to its original idle label — no error banner, no lasting
          // indication anything failed, indistinguishable from having never clicked at all.
          // Now "TRY AGAIN" persists (button is already interactive — is-loading was
          // removed above) until the customer clicks again, which restarts the flow.
          btn.textContent = 'TRY AGAIN';
          console.error('[collection-museum-gallery] Add to cart error', {
            variantId: parsedVariantId,
            error: err.message,
            stack: err.stack,
            durationMs: cartAddDurationMs.toFixed(0),
            attemptNumber: atcAttemptCount,
          });
        });
    });

    /* ============================================================
       UPDATE CART COUNT (cached DOM queries + PubSub integration)
       ============================================================ */

    let cartCountElementCache = null;

    /**
     * Get all cart count display elements, caching query result
     * Queries for [data-cart-count] or .cart-count and caches NodeList.
     * Without caching: every add-to-cart event = DOM traversal. With caching: O(1) lookup.
     *
     * @returns {NodeList} Cart count display elements
     */
    function getCartCountElements() {
      if (!cartCountElementCache) {
        cartCountElementCache = document.querySelectorAll('[data-cart-count], .cart-count');
      }
      return cartCountElementCache;
    }

    /**
     * Publish cart update event to FoxTheme PubSub (if available)
     * Integrates with Fox theme's event system so other components (cart drawer, etc.) react to cart changes.
     * Safely checks for FoxTheme.pubsub existence before publishing to prevent errors if theme not loaded.
     *
     * @param {Object} cartData - Cart object from /cart.js endpoint
     */
    function publishCartUpdateEvent(cartData) {
      if (
        window.FoxTheme &&
        window.FoxTheme.pubsub &&
        window.FoxTheme.pubsub.publish &&
        window.FoxTheme.pubsub.PUB_SUB_EVENTS &&
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
      ) {
        try {
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: cartData,
          });
        } catch (pubsubErr) {
          console.error('[collection-museum-gallery] PubSub publish error:', pubsubErr);
        }
      }
    }

    /**
     * Fetch current cart state and update all cart count displays
     * Called after add-to-cart to reflect new item count across the page.
     * Validates response structure (item_count must be non-negative integer).
     * Updates all cart count elements in single pass (getCartCountElements cache).
     * Publishes PubSub event for FoxTheme integration (cart drawer auto-refresh, etc.).
     */
    let cartCountUpdateCount = 0;

    function updateCartCount() {
      const cartFetchStartTime = performance.now();
      cartCountUpdateCount++;

      return window.GalleryUtils.fetchJson('/cart.js')
        .then(function (cart) {
          const cartFetchDurationMs = performance.now() - cartFetchStartTime;
          // SECURITY: Validate cart response structure before use
          if (!cart || typeof cart !== 'object' || typeof cart.item_count === 'undefined') {
            console.warn(
              '[collection-museum-gallery] Cart response invalid or missing item_count',
              {
                updateNumber: cartCountUpdateCount,
                durationMs: cartFetchDurationMs.toFixed(0),
                response: cart,
              }
            );
            return;
          }
          const itemCount = parseInt(cart.item_count, 10);
          if (isNaN(itemCount) || itemCount < 0) {
            console.warn('[collection-museum-gallery] Cart item_count is invalid', {
              raw: cart.item_count,
              parsed: itemCount,
              updateNumber: cartCountUpdateCount,
            });
            return;
          }
          // Update all cart count displays in DOM with cached query
          const countEls = getCartCountElements();
          countEls.forEach(function (el) {
            el.textContent = itemCount;
          });
          if (window.__DEBUG_GALLERY) {
            console.warn('[collection-museum-gallery] Cart count updated', {
              newCount: itemCount,
              elementsUpdated: countEls.length,
              durationMs: cartFetchDurationMs.toFixed(0),
              updateNumber: cartCountUpdateCount,
            });
          }
          publishCartUpdateEvent(cart);
        })
        .catch(function (err) {
          const cartFetchDurationMs = performance.now() - cartFetchStartTime;
          console.error('[collection-museum-gallery] Update cart count error', {
            error: err.message,
            stack: err.stack,
            durationMs: cartFetchDurationMs.toFixed(0),
            updateNumber: cartCountUpdateCount,
          });
        });
    }

    /* ============================================================
       GLOBAL LISTENER CLEANUP
       The Fox theme uses AJAX navigation (dispatches page:loaded after content
       swap) without re-executing scripts already loaded. Without explicit cleanup,
       window/document listeners registered here survive navigation to non-gallery
       pages. updateHeader in particular hides the site header on scroll for ALL
       page types — wrong outside the museum gallery context.
       ============================================================ */

    function removeGlobalListeners() {
      globalListeners.forEach(function (entry) {
        // A null target means entry[1] is a window.CoraScroll.subscribe()
        // unsubscribe closure rather than a (type, fn, target) DOM listener
        // triple - both go through this one teardown path so cleanup cannot
        // miss one of them on AJAX navigation away from the gallery.
        if (entry[2]) {
          entry[2].removeEventListener(entry[0], entry[1]);
        } else {
          entry[1]();
        }
      });
      globalListeners.length = 0;
    }

    document.addEventListener('page:loaded', function onPageLoaded() {
      if (!document.getElementById('MuseumGallery')) {
        removeGlobalListeners();
        document.removeEventListener('page:loaded', onPageLoaded);
      }
    });

    window.addEventListener('pagehide', removeGlobalListeners, { once: true });
  }

  const galleryInitStartTime = performance.now();
  if (window.__DEBUG_GALLERY) {
    console.warn('[collection-museum-gallery] Module loaded, initializing...');
  }

  function initWithMetrics() {
    const initDurationMs = performance.now() - galleryInitStartTime;
    init();
    if (window.__DEBUG_GALLERY) {
      console.warn('[collection-museum-gallery] Init complete', {
        durationMs: initDurationMs.toFixed(0),
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithMetrics);
  } else {
    initWithMetrics();
  }
})();
